/**
 * @module public/js/focusmode/phoneOrder.js
 * @summary Shared helpers for Focus Mode phone-order configuration.
 */

/**
 * Canonical phone-slot order used when no sequence override exists.
 *
 * @type {readonly string[]}
 */
export const DEFAULT_FOCUSMODE_PHONE_ORDER = Object.freeze([
  'mobilePhone',
  'officePhone',
  'mainOfficePhone',
]);

/**
 * Display metadata for the supported Focus Mode phone slots.
 *
 * @type {ReadonlyArray<{key: string, label: string}>}
 */
export const FOCUSMODE_PHONE_ORDER_OPTIONS = Object.freeze([
  { key: 'mobilePhone', label: 'Mobile' },
  { key: 'officePhone', label: 'Office' },
  { key: 'mainOfficePhone', label: 'Main Office' },
]);

/**
 * Normalize a raw Focus Mode phone-order payload into a complete, valid array.
 *
 * Unknown values are ignored, duplicates are removed, and any missing
 * canonical keys are appended in the default order so callers always receive
 * a stable three-item sequence.
 *
 * @param {unknown} rawOrder - Raw stored or user-provided ordering.
 * @returns {string[]} Normalized phone-order keys.
 */
export function normalizeFocusModePhoneOrder(rawOrder) {
  const seen = new Set();
  const normalized = [];
  const source = Array.isArray(rawOrder) ? rawOrder : [];

  for (const value of source) {
    const key = `${value ?? ''}`.trim();
    if (DEFAULT_FOCUSMODE_PHONE_ORDER.includes(key) && !seen.has(key)) {
      normalized.push(key);
      seen.add(key);
    }
  }

  for (const key of DEFAULT_FOCUSMODE_PHONE_ORDER) {
    if (!seen.has(key)) normalized.push(key);
  }

  return normalized;
}

/**
 * Return the available phone-slot keys in the requested order.
 *
 * Blank and missing values are excluded so callers can hide or skip phone
 * types that do not currently have a dialable value.
 *
 * @param {Record<string, unknown>} values - Current phone values keyed by slot.
 * @param {unknown} rawOrder - Raw order preference to apply.
 * @returns {string[]} Ordered keys that currently have a value.
 */
export function listAvailableFocusModePhoneOrder(values = {}, rawOrder) {
  const order = normalizeFocusModePhoneOrder(rawOrder);
  return order.filter((key) => `${values?.[key] ?? ''}`.trim().length > 0);
}
